import {CartLinesUpdateEvent, StandardEvents} from '@shopify/events';
import {morphSection, sectionRenderer} from '@theme/section-renderer';
const {buildTargets, cartStamp, selectedGroups, restoreSelections, desiredLines, reconcileOperations, planMatches, withCartProperties, pendingManualColors, withReviewIdentities} =
  await import(document.querySelector('script[data-paint-module]').dataset.modelUrl);

const node = (tag, text, className) => {
  const element = document.createElement(tag);
  if (text !== undefined) element.textContent = text;
  if (className) element.className = className;
  return element;
};

class CustomPaintOrdering extends HTMLElement {
  async connectedCallback() {
    if (this.initialized) {
      if (this.onCartUpdate) document.addEventListener(StandardEvents.cartLinesUpdate, this.onCartUpdate);
      if (this.onCheckout) document.addEventListener('submit', this.onCheckout, true);
      if (this.onCheckout) this.syncCheckoutState();
      return;
    }
    this.initialized = true;
    this.announcement = this.announcementRegion(this.closest('dialog') || document.body);
    this.data = JSON.parse(this.querySelector('[data-paint-data]').textContent);
    this.status = this.querySelector('[data-paint-status]');
    this.controls = this.querySelector('[data-paint-controls]');
    try { this.data = await this.readInitialData(); }
    catch (error) {
      this.message(error.message === 'cart_changed' ? 'cart_changed' : 'load_failed');
      this.syncCheckoutState(this.data.lines.some(line => line.service));
      return;
    }
    if (!this.isConnected) {
      this.initialized = false;
      return;
    }
    this.targets = buildTargets(this.data);
    const restored = restoreSelections(this.data, this.targets);
    this.selections = restored.selections;
    this.notes = this.querySelector('[data-paint-notes]');
    this.notes.value = restored.notes;
    this.dialog = this.querySelector('[data-paint-dialog]');
    this.dialog.addEventListener('keydown', event => {
      if (event.key !== 'Escape' || event.isComposing) return;
      event.preventDefault();
      event.stopPropagation();
      this.dialog.close();
    });
    this.saveButton = this.querySelector('[data-paint-save]');
    this.allSelect = this.querySelector('[data-paint-all]');
    this.dialogStatus = this.querySelector('[data-paint-dialog-status]');
    this.blocked = !this.data.serviceId || !this.data.colors.length || restored.unmanaged || this.data.lines.some(line => line.unmappedBundle);
    this.savedInvalid = restored.invalid;
    this.dirty = false;
    this.edited = false;
    this.busy = false;
    this.rowSelects = new Map();
    this.controls.hidden = !this.targets.length;
    this.populateColors(this.allSelect);
    this.allSelect.append(new Option(this.label('mixed'), '__mixed', false, false));
    this.allSelect.options[this.allSelect.options.length - 1].disabled = true;
    this.renderGroups();
    if (this.blocked) this.message(restored.unmanaged ? 'unmanaged_service' : this.data.lines.some(line => line.unmappedBundle) ? 'unmapped' : 'load_failed');
    else if (this.savedInvalid) this.message('saved_invalid');
    else if (!this.targets.length) this.message(this.data.lines.some(line => line.service) ? 'empty_service' : 'no_parts');
    this.allSelect.addEventListener('change', () => {
      this.edited = true;
      for (const target of this.targets) this.selections.set(target.id, this.allSelect.value);
      for (const [id, select] of this.rowSelects) select.value = this.selections.get(id) || '';
      this.dirty = true;
      this.savedInvalid = false;
      this.updateSummary();
    });
    this.notes.addEventListener('input', () => { this.edited = true; this.updateSummary(); });
    this.querySelector('[data-paint-review]').addEventListener('click', () => this.review());
    this.saveButton.addEventListener('click', () => this.save());
    this.onCartUpdate = event => {
      if (this.busy) return;
      this.refreshing = true;
      this.syncCheckoutState();
      Promise.resolve(event.promise).then(() => this.refresh()).catch(() => {
        this.savedInvalid = true;
        this.message('load_failed');
      }).finally(() => {
        this.refreshing = false;
        this.syncCheckoutState();
      });
    };
    this.onCheckout = event => {
      if (!this.closest('.cart__ctas')?.contains(event.submitter)) return;
      if (!this.dirty && !this.savedInvalid && !this.busy && !this.refreshing) return;
      if (event.submitter?.name !== 'checkout') return;
      event.preventDefault();
      event.stopImmediatePropagation();
      this.message('save_first');
      this.querySelector('[data-paint-disclosure]').open = true;
      this.querySelector('[data-paint-review]').focus();
    };
    document.addEventListener(StandardEvents.cartLinesUpdate, this.onCartUpdate);
    document.addEventListener('submit', this.onCheckout, true);
    this.updateSummary();
  }

  disconnectedCallback() {
    if (this.onInitialCartUpdate) document.removeEventListener(StandardEvents.cartLinesUpdate, this.onInitialCartUpdate);
    document.removeEventListener(StandardEvents.cartLinesUpdate, this.onCartUpdate);
    document.removeEventListener('submit', this.onCheckout, true);
    this.restoreCheckoutState();
  }

  propertyLabels() {
    return {
      colorProperty: this.label('color_property'), dispatchProperty: this.label('dispatch_property'),
      partsProperty: this.label('parts_property'), requestsProperty: this.label('requests_property'), dispatch: this.label('dispatch')
    };
  }

  restoreCheckoutState() {
    for (const [element, state] of this.checkoutLocks || []) {
      const requiresParts = element.hasAttribute?.('data-paint-requires-parts');
      if (state.disabled !== null) element.disabled = Boolean(requiresParts || state.disabled);
      element.inert = state.inert;
      if (!requiresParts) {
        for (const [name, value] of Object.entries(state.attributes)) {
          if (value === null) element.removeAttribute(name);
          else element.setAttribute(name, value);
        }
      }
      element.removeAttribute('data-paint-checkout-locked');
    }
    this.checkoutLocks?.clear();
  }

  syncCheckoutState(locked = Boolean(this.dirty || this.savedInvalid || this.busy || this.refreshing)) {
    const paintWithoutParts = Boolean(this.data?.lines.some(line => line.service) && !buildTargets(this.data).length);
    locked ||= paintWithoutParts;
    const notice = this.querySelector('[data-paint-checkout-status]');
    notice.hidden = !locked;
    if (this.data?.labels) notice.textContent = this.label(paintWithoutParts ? 'empty_service' : 'save_first');
    if (this.status) this.status.hidden = paintWithoutParts;
    if (!this.busy && !this.refreshing) {
      const disclosure = this.querySelector('[data-paint-disclosure]');
      if (disclosure) {
        if (!locked && disclosure.open && disclosure.contains(document.activeElement)) {
          disclosure.querySelector('summary')?.focus({preventScroll: true});
        }
        disclosure.open = locked;
      }
    }
    const container = this.closest('.cart__ctas');
    const button = container?.querySelector('button[name="checkout"]');
    if (button?.hasAttribute?.('data-paint-requires-parts') && !paintWithoutParts && !this.refreshing) {
      const state = this.checkoutLocks?.get(button);
      button.removeAttribute('data-paint-requires-parts');
      button.disabled = !this.data?.lines.length || Boolean(state?.disabled);
      const descriptions = (button.getAttribute('aria-describedby') || '').split(/\s+/).filter(id => id && id !== notice.id);
      if (descriptions.length) button.setAttribute('aria-describedby', descriptions.join(' '));
      else button.removeAttribute('aria-describedby');
      if (state) {
        state.disabled = button.disabled;
        state.attributes['aria-describedby'] = button.getAttribute('aria-describedby');
      }
    }
    if (!button || !locked) {
      this.restoreCheckoutState();
      return;
    }
    const controls = [button, container.querySelector('.additional-checkout-buttons')].filter(Boolean);
    if (this.checkoutLocks && [...this.checkoutLocks.keys()].some(element => !controls.includes(element))) this.restoreCheckoutState();
    this.checkoutLocks ||= new Map();
    for (const control of controls) {
      if (!this.checkoutLocks.has(control)) this.checkoutLocks.set(control, {
        disabled: control === button ? (control.hasAttribute?.('data-paint-requires-parts') ? false : control.disabled) : null, inert: control.inert,
        attributes: {'aria-disabled': control.getAttribute('aria-disabled'), 'aria-describedby': control.getAttribute('aria-describedby')}
      });
      if (control === button) {
        if (paintWithoutParts) control.setAttribute('data-paint-requires-parts', '');
        control.disabled = true;
      }
      else control.inert = true;
      control.setAttribute('aria-disabled', 'true');
      control.setAttribute('data-paint-checkout-locked', '');
      const descriptions = new Set((control.getAttribute('aria-describedby') || '').split(/\s+/).filter(Boolean));
      descriptions.add(notice.id);
      control.setAttribute('aria-describedby', [...descriptions].join(' '));
    }
  }

  label(key, values = {}) {
    let text = this.data.labels[key] || key;
    for (const [name, value] of Object.entries(values)) text = text.replaceAll(`__${name.toUpperCase()}__`, String(value));
    return text;
  }

  message(key, dialog = false) {
    (dialog ? this.dialogStatus : this.status).textContent = this.label(key);
  }

  announcementRegion(host) {
    let region = host.querySelector(':scope > [data-paint-save-announcement]');
    if (!region) {
      region = node('div', undefined, 'visually-hidden');
      region.setAttribute('data-paint-save-announcement', '');
      region.setAttribute('role', 'status');
      region.setAttribute('aria-live', 'polite');
      region.setAttribute('aria-atomic', 'true');
      host.append(region);
    }
    return region;
  }

  completeInlineSave(restoreFocus) {
    const current = document.getElementById(this.id);
    const active = document.activeElement;
    const drawerDialog = current?.closest('dialog');
    const target = drawerDialog && !drawerDialog.open
      ? document.querySelector('[data-testid="cart-drawer-trigger"]')
      : current?.querySelector('[data-paint-disclosure] > summary');
    if (restoreFocus && target && (!active || active === document.body || this.contains(active) || current?.contains(active))) {
      target.focus();
    }
    this.status.textContent = '';
    const announcement = this.announcementRegion(drawerDialog?.open ? drawerDialog : document.body);
    if (this.announcement !== announcement) this.announcement.textContent = '';
    announcement.textContent = this.label('saved_inline');
    this.announcement = announcement;
  }

  money(amount) {
    return new Intl.NumberFormat(this.data.locale, {style: 'currency', currency: this.data.currency}).format(amount / 100);
  }

  populateColors(select) {
    for (const color of this.data.colors) {
      const option = new Option(color.title, String(color.id));
      option.disabled = !color.available;
      select.append(option);
    }
  }

  renderGroups() {
    const container = this.querySelector('[data-paint-groups]');
    container.replaceChildren();
    this.rowSelects.clear();
    const groups = new Map();
    for (const target of this.targets) {
      if (!groups.has(target.lineKey)) groups.set(target.lineKey, []);
      groups.get(target.lineKey).push(target);
    }
    let index = 0;
    for (const targets of groups.values()) {
      const details = node('details', undefined, 'paint-ordering__group');
      const summary = node('summary');
      summary.append(node('span', targets[0].parentTitle), this.querySelector('[data-paint-chevron]').content.cloneNode(true));
      details.append(summary);
      for (const target of targets) {
        const row = node('div', undefined, 'paint-ordering__part');
        const select = node('select');
        select.id = `PaintPart-${this.dataset.sectionId}-${index++}`;
        select.append(new Option(this.label('original'), ''));
        this.populateColors(select);
        select.value = this.selections.get(target.id) || '';
        const label = node('label');
        label.htmlFor = select.id;
        label.append(node('span', target.title), node('span', target.sku, 'paint-ordering__sku'));
        if (target.quantity > 1) label.append(node('span', this.label('unit', {number: target.unit + 1, quantity: target.quantity}), 'paint-ordering__sku'));
        if (target.image) {
          const image = node('img');
          image.src = target.image;
          image.alt = '';
          image.width = 56;
          image.height = 56;
          image.loading = 'lazy';
          row.append(image);
        } else row.append(node('span'));
        row.append(label, select);
        select.addEventListener('change', () => {
          this.edited = true;
          this.selections.set(target.id, select.value);
          this.dirty = true;
          this.savedInvalid = false;
          this.updateSummary();
        });
        this.rowSelects.set(target.id, select);
        details.append(row);
      }
      container.append(details);
    }
  }

  groups() { return selectedGroups(this.targets, this.selections, this.data.colors); }

  updateSummary() {
    try {
      const groups = this.groups();
      this.manualPending = pendingManualColors(this.data, groups).length > 0;
      this.dirty = !planMatches(this.data, desiredLines(groups, this.notes.value, this.propertyLabels(), this.targets));
      const total = groups.reduce((sum, group) => sum + group.color.price, 0);
      this.querySelector('[data-paint-summary]').textContent = groups.length
        ? this.label('summary', {count: groups.length, price: this.money(total)})
        : this.label('no_colors');
      const values = new Set(this.targets.map(target => this.selections.get(target.id) || ''));
      this.allSelect.value = values.size === 1 ? [...values][0] : '__mixed';
      if (this.manualPending && this.targets.length) this.message('manual_assignments');
      else if (this.status.textContent === this.label('manual_assignments')) this.status.textContent = '';
      this.querySelector('[data-paint-review]').disabled = this.blocked || this.busy || this.manualPending;
      this.saveButton.disabled = this.blocked || this.busy || this.manualPending;
    } catch {
      this.dirty = true;
      this.message('color_unavailable');
    }
    this.syncCheckoutState();
  }

  review() {
    if (this.blocked || this.busy || this.manualPending) return;
    const content = this.querySelector('[data-paint-review-content]');
    content.replaceChildren();
    try {
      const groups = this.groups();
      if (!groups.length) content.append(node('p', this.label('no_colors')));
      for (const {color, targets} of groups) {
        const group = node('div', undefined, 'paint-ordering__review-color');
        group.append(node('strong', `${color.title} - ${this.money(color.price)}`));
        const list = node('ul');
        const counts = new Map();
        for (const target of targets) counts.set(target.sku, (counts.get(target.sku) || 0) + 1);
        for (const [sku, count] of counts) list.append(node('li', `${sku} x ${count}`));
        group.append(list);
        content.append(group);
      }
      if (this.notes.value.trim()) content.append(node('p', `${this.label('requests')}: ${this.notes.value.trim()}`));
      content.append(node('strong', this.querySelector('[data-paint-summary]').textContent));
      this.dialogStatus.textContent = '';
      this.dialog.showModal();
    } catch { this.message('color_unavailable'); }
  }

  async readInitialData() {
    // An add/remove can finish while the first cart snapshot is loading.
    let revision = 0;
    let pending = Promise.resolve();
    const onUpdate = event => {
      revision++;
      pending = Promise.resolve(event.promise).catch(() => {});
    };
    this.onInitialCartUpdate = onUpdate;
    document.addEventListener(StandardEvents.cartLinesUpdate, onUpdate);
    try {
      for (let attempt = 0; attempt < 3; attempt++) {
        const before = revision;
        await pending;
        try {
          const data = await this.readData();
          if (before === revision) return data;
        } catch (error) {
          if (error.message !== 'cart_changed' || before === revision) throw error;
        }
      }
      throw new Error('cart_changed');
    } finally {
      document.removeEventListener(StandardEvents.cartLinesUpdate, onUpdate);
      if (this.onInitialCartUpdate === onUpdate) this.onInitialCartUpdate = undefined;
    }
  }

  async readData() {
    const url = new URL(window.location.href);
    url.searchParams.set('section_id', this.dataset.sectionId);
    url.searchParams.set('_paint_read', String(Date.now()));
    const response = await fetch(url, {headers: {Accept: 'text/html'}, cache: 'no-store', signal: AbortSignal.timeout(20000)});
    if (!response.ok) throw new Error('load_failed');
    const html = await response.text();
    const document = new DOMParser().parseFromString(html, 'text/html');
    const element = document.querySelector('[data-paint-data]');
    if (!element) throw new Error('load_failed');
    const data = JSON.parse(element.textContent);
    // Liquid serializes properties as escaped pairs; the Ajax cart preserves their exact values.
    const cartUrl = new URL(data.root.replace(/\/?$/, '/') + 'cart.js', window.location.origin);
    cartUrl.searchParams.set('_paint_read', String(Date.now()));
    const cartResponse = await fetch(cartUrl, {headers: {Accept: 'application/json'}, cache: 'no-store', signal: AbortSignal.timeout(20000)});
    if (!cartResponse.ok) throw new Error('load_failed');
    const cart = await cartResponse.json();
    const canonical = await withReviewIdentities(withCartProperties(data, cart));
    this.snapshot = {html, cart};
    return canonical;
  }

  async updateCartSection() {
    const component = this.closest('cart-items-component');
    if (!component || !this.snapshot) throw new Error('load_failed');
    const {html, cart} = this.snapshot;
    const update = CartLinesUpdateEvent.createPromise();
    this.dialog.close();
    component.dispatchEvent(new CartLinesUpdateEvent({
      action: 'update', context: 'cart',
      lines: cart.items.filter(item => String(item.product_id) === this.data.serviceId).map(item => ({id: item.key, quantity: item.quantity})),
      promise: update.promise
    }));
    try {
      sectionRenderer.abortRender(this.dataset.sectionId);
      await morphSection(this.dataset.sectionId, html, {
        mode: component.hasAttribute('data-drawer') ? 'hydration' : 'full', injectStylesheet: true
      });
      update.resolve({
        cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
        detail: {sections: {[this.dataset.sectionId]: html}, items: cart.items, itemCount: cart.item_count, source: 'custom-paint-ordering', didError: false}
      });
    } catch (error) {
      update.reject(error);
      throw error;
    }
  }

  async refresh() {
    if (!this.isConnected) return;
    const fresh = await this.readData();
    const sameCart = cartStamp(fresh) === cartStamp(this.data);
    const serviceChanged = JSON.stringify(fresh.lines.filter(line => line.service)) !== JSON.stringify(this.data.lines.filter(line => line.service));
    const needsReview = this.data.lines.some(line => line.service) || [...this.selections.values()].some(Boolean);
    const previousSelections = new Map(this.targets.map(target => [target.reviewId, this.selections.get(target.id) || '']));
    this.data = fresh;
    this.targets = buildTargets(fresh);
    const restored = restoreSelections(fresh, this.targets);
    if (!sameCart || serviceChanged) {
      this.selections = this.edited
        ? new Map(this.targets.map(target => [target.id, previousSelections.has(target.reviewId) ? previousSelections.get(target.reviewId) : restored.selections.get(target.id) || '']))
        : restored.selections;
      if (!this.edited) this.notes.value = restored.notes;
      this.dialog.close();
      this.renderGroups();
    }
    this.blocked = !fresh.serviceId || !fresh.colors.length || restored.unmanaged || fresh.lines.some(line => line.unmappedBundle);
    const hasPaintRequest = fresh.lines.some(line => line.service) || [...this.selections.values()].some(Boolean);
    this.savedInvalid = hasPaintRequest && restored.invalid;
    this.controls.hidden = !this.targets.length;
    this.updateSummary();
    if (!sameCart && needsReview && hasPaintRequest && !this.manualPending) this.message('cart_changed');
  }

  setBusy(value) {
    this.busy = value;
    this.setAttribute('aria-busy', String(value));
    this.querySelectorAll('button, select, textarea').forEach(control => { control.disabled = value; });
    if (!value) {
      for (const select of this.querySelectorAll('select')) for (const option of select.options) {
        if (option.value === '__mixed') option.disabled = true;
      }
    }
    this.updateSummary();
  }

  async mutate(operation) {
    const root = this.data.root.replace(/\/?$/, '/');
    const path = operation.kind === 'add' ? 'cart/add.js' : 'cart/change.js';
    const payload = operation.kind === 'add' ? {items: [operation.item]} : {
      id: operation.key, quantity: operation.quantity,
      ...(operation.kind === 'change' ? {properties: operation.properties} : {})
    };
    const response = await fetch(root + path, {
      method: 'POST', headers: {Accept: 'application/json', 'Content-Type': 'application/json'},
      body: JSON.stringify(payload), signal: AbortSignal.timeout(20000)
    });
    if (!response.ok) throw new Error('save_unknown');
    await response.json();
  }

  async save() {
    if (this.busy || this.blocked || this.manualPending) return;
    let attempted = false;
    let savedInline = false;
    const restoreFocus = this.dialog.contains(document.activeElement);
    this.announcement.textContent = '';
    this.setBusy(true);
    this.message('saving', true);
    try {
      let current = await this.readData();
      const physicalStamp = cartStamp(this.data);
      if (cartStamp(current) !== physicalStamp) throw new Error('cart_changed');
      const desired = desiredLines(this.groups(), this.notes.value, this.propertyLabels(), this.targets);
      const operations = reconcileOperations(current, desired);
      for (const operation of operations) {
        const gate = await this.readData();
        if (JSON.stringify(gate.lines) !== JSON.stringify(current.lines)) throw new Error('cart_changed');
        attempted = true;
        await this.mutate(operation);
        current = await this.readData();
        if (cartStamp(current) !== physicalStamp) throw new Error('cart_changed');
      }
      if (!planMatches(current, desired)) throw new Error('save_unknown');
      this.data = current;
      this.dirty = false;
      this.savedInvalid = false;
      this.edited = false;
      if (this.closest('cart-items-component')) {
        await this.updateCartSection();
        savedInline = true;
        return;
      }
      this.message('saved', true);
      const url = new URL(this.data.cartUrl, window.location.origin);
      url.searchParams.set('view', 'custom-paint-review');
      const previewId = new URL(window.location.href).searchParams.get('preview_theme_id');
      if (previewId) url.searchParams.set('preview_theme_id', previewId);
      window.location.assign(url.href);
    } catch (error) {
      const key = error.message === 'cart_changed' ? 'cart_changed' :
        error.message === 'unmanagedService' ? 'unmanaged_service' :
        error.message === 'manualUnassigned' ? 'manual_assignments' :
        error.message === 'savedInvalid' ? 'saved_invalid' :
        error.message === 'selectionTooLarge' ? 'selection_too_large' :
        error.message === 'colorUnavailable' ? 'color_unavailable' :
        attempted ? 'save_unknown' : 'load_failed';
      this.message(key, true);
      if (attempted) this.blocked = true;
    } finally {
      this.setBusy(false);
      if (savedInline) this.completeInlineSave(restoreFocus);
      else if (restoreFocus && this.dialog.open && document.activeElement === document.body) this.saveButton.focus();
    }
  }
}

if (!customElements.get('custom-paint-ordering')) customElements.define('custom-paint-ordering', CustomPaintOrdering);
